import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { API } from "../constants";

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios.get(`${API}/testimonials`)
      .then(r => setTestimonials(r.data))
      .catch(() => {});
  }, []);

  if (!testimonials.length) return null;

  return (
    <section className="py-20 bg-[#0A0A0A] border-t border-white/5" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl tracking-wide mb-3">WHAT OUR CUSTOMERS SAY</h2>
          <p className="text-gray-400">Real results from real polishers</p>
        </div>

        {/* Featured testimonial */}
        <div className="relative max-w-3xl mx-auto mb-10">
          <div className="bg-[#121212] border border-white/10 rounded-sm p-8 sm:p-10 relative overflow-hidden" data-testid={`testimonial-featured-${active}`}>
            <Quote className="absolute top-6 right-8 w-16 h-16 text-[#D62828]/10" />
            <div className="flex items-center gap-1 mb-5">
              {[...Array(testimonials[active]?.rating || 5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
              ))}
            </div>
            <p className="text-gray-200 text-lg sm:text-xl leading-relaxed mb-6 italic">
              "{testimonials[active]?.content}"
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D62828] rounded-full flex items-center justify-center font-bold text-white">
                {testimonials[active]?.author?.[0]}
              </div>
              <div>
                <p className="font-semibold text-white">{testimonials[active]?.author}</p>
                {testimonials[active]?.location && (
                  <p className="text-gray-500 text-sm">{testimonials[active]?.location}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dot navigation */}
        <div className="flex justify-center gap-2 mb-10">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${i === active ? 'bg-[#D62828] w-6' : 'bg-white/20 hover:bg-white/40'}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>

        {/* All cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <div
              key={t.testimonial_id}
              onClick={() => setActive(i)}
              className={`bg-[#0F0F0F] border rounded-sm p-5 cursor-pointer transition-all ${i === active ? 'border-[#D62828]/50' : 'border-white/5 hover:border-white/20'}`}
              data-testid={`testimonial-card-${t.testimonial_id}`}
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 text-[#D4AF37] fill-[#D4AF37]" />
                ))}
              </div>
              <p className="text-gray-400 text-sm leading-relaxed line-clamp-3 mb-4">"{t.content}"</p>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#D62828] rounded-full flex items-center justify-center text-xs font-bold text-white">
                  {t.author[0]}
                </div>
                <div>
                  <p className="text-white text-xs font-semibold">{t.author}</p>
                  {t.location && <p className="text-gray-600 text-xs">{t.location}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
